import React, { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { assemblyTemplate as assemblyTemplateState } from '@/components/Analysis/state';
import {
  EquipmentDetailTypeEnum,
  OptionFieldTypeEnum,
  EquipmentDetailSubTypeEnum,
} from '@/__generated__/graphql';
import { getTemplateFieldData } from '@/utils';
import { Detail, DetailsAccordion } from '../StyledComponents';
import { EquipmentCondition } from '../EquipmentCondition';
import { SelectUpdater } from './OptionFieldDisplays';
import { DatePickerForComponentSection } from './DateUpdater';
import { useAuth0 } from '@auth0/auth0-react';
import { StyledDivider } from '../styles';

type Props = { data: { [key: string]: string }; area: number };

const { SimpleText, CircularProgressBar } = OptionFieldTypeEnum;

export const EquipmentDetailRenderer = ({ data, area }: Props) => {
  const assemblyTemplate = useAtomValue(assemblyTemplateState);
  const equipmentDetails = assemblyTemplate?.equipmentDetails;
  const { user } = useAuth0();

  const userRoles = user?.['http://abyss.com/user/roles'] as string[] | undefined;

  const defectConsolidatedDegree = useMemo(() => {
    const consolidatedDegreeOfRusting = equipmentDetails?.find(
      ({ subType }) => subType === EquipmentDetailSubTypeEnum.DefectConsolidatedDegreeOfRusting
    );
    if (!consolidatedDegreeOfRusting) return <></>;

    const title = data?.consolidated_degree_of_rusting ?? 'N/A';
    const ratio = data?.corrosion_ratio ?? 'N/A';

    return (
      <DetailsAccordion title={consolidatedDegreeOfRusting.name}>
        <EquipmentCondition title={title} valueInt={ratio} area={area} />
      </DetailsAccordion>
    );
  }, [equipmentDetails, data, area]);

  const { templateListSimpleText, templateListProgressBar } = useMemo(() => {
    const templateList = equipmentDetails?.filter(
      ({ type }) => type === EquipmentDetailTypeEnum.TemplateList
    );

    if (templateList?.length === 0) {
      return { templateListSimpleText: [], templateListProgressBar: [] };
    }

    const simpleTextFields = templateList?.flatMap((item) => {
      return item.options.filter((option) => option.type === SimpleText);
    });

    const simpleTextFieldsData = getTemplateFieldData(simpleTextFields, data).filter(
      ({ templateKey }) => templateKey !== 'eq_details_nominal_corrosion_allowance'
    );

    const simpleTextComponent = simpleTextFieldsData.map((item, index) => {
      const field = assemblyTemplate?.fields.find((f) => f.key === item.templateKey);
      // Roles stored on the field, if the user has one of these roles, they can edit the field
      // theres also a check on the backend to ensure the user has the correct role
      const editRoles = field?.editRoles;
      const userHasOneOfEditRoles = userRoles?.some((role) => editRoles?.includes(role));
      const shouldShowEditableField = field?.isEditable && userHasOneOfEditRoles;
      const isDateField = field?.displayConfig?.type === 'DATE';

      const { name, value } = item;

      // Check if the field has a detailFieldKey (assignment field) and get its value
      let subtitle: string | undefined;
      if (field?.detailFieldKey) {
        const assignmentFieldValue = data[field.detailFieldKey];
        if (assignmentFieldValue) {
          subtitle = assignmentFieldValue;
        }
      }

      return (
        <React.Fragment key={index}>
          {shouldShowEditableField && isDateField && <DatePickerForComponentSection {...item} data={data} />}
          {shouldShowEditableField && !isDateField && <SelectUpdater {...item} data={data} />}
          {!shouldShowEditableField && name && value !== null && value !== undefined && (
            <Detail
              key={name}
              title={name!!}
              value={value === '' ? 'N/A' : value}
              insideAccordion={false}
              subtitle={subtitle}
            />
          )}
        </React.Fragment>
      );
    });

    const progressFieldComponent = templateList
      ?.filter(({ name }) => name)
      .map((item) => {
        const fields = item.options.filter((option) => option.type === CircularProgressBar);
        const fieldsData = getTemplateFieldData(fields, data);

        return (
          <DetailsAccordion title={item.name} key={item.name}>
            {fieldsData.map((field) => (
              <EquipmentCondition
                key={field.name}
                title={field.name ?? ''}
                valueInt={field.value ?? 0}
                area={area}
              />
            ))}
          </DetailsAccordion>
        );
      });

    return {
      templateListSimpleText: simpleTextComponent,
      templateListProgressBar: progressFieldComponent,
    };
  }, [area, assemblyTemplate?.fields, data, equipmentDetails, userRoles]);

  return (
    <>
      {templateListSimpleText}
      <StyledDivider />
      {templateListProgressBar}
      {defectConsolidatedDegree}
    </>
  );
};
